import React, { useEffect, useRef, useState } from "react";
import { useCookieConsent } from "../context/CookieConsentContext";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import ModalCloseButton from "./ModalCloseButton";
import { useTranslation } from "react-i18next";

const CookieBanner = () => {
  const { isBannerOpen, prefs, acceptAll, rejectAll, savePrefs, closeBanner } = useCookieConsent();
  const { t } = useTranslation();
  const location = useLocation();
  const [localPrefs, setLocalPrefs] = useState(prefs);
  const [isCustomizeOpen, setCustomizeOpen] = useState(false);
  const modalRef = useRef(null);
  const policyPath = location.pathname.startsWith("/en")
    ? "/en/cookie-policy"
    : "/cookie-policy";

  useEffect(() => {
    if (isCustomizeOpen) {
      document.body.classList.add('cookie-modal-open');
      const previouslyFocusedElement = document.activeElement;
      const frameId = window.requestAnimationFrame(() => {
        modalRef.current?.querySelector("button")?.focus();
      });

      const handleKeyDown = (event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          setCustomizeOpen(false);
          return;
        }

        if (event.key !== "Tab" || !modalRef.current) return;
        const focusableElements = Array.from(
          modalRef.current.querySelectorAll(
            'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
          )
        );
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => {
        window.cancelAnimationFrame(frameId);
        document.removeEventListener("keydown", handleKeyDown);
        document.body.classList.remove('cookie-modal-open');
        previouslyFocusedElement?.focus?.();
      };
    }

    document.body.classList.remove('cookie-modal-open');
    return undefined;
  }, [isCustomizeOpen]);

  if (!isBannerOpen) return null;

  const onToggle = (key) => setLocalPrefs((p) => ({ ...p, [key]: !p[key] }));

  /* Portal to body: .app uses transform for lang-fade, which makes fixed descendants
   * position against .app — cookie bar would sit at the document bottom (off-screen). */
  const bannerPortal = createPortal(
    <div
      className={`cookie-banner ${isCustomizeOpen ? "modal-open" : ""}`}
      role="dialog"
      aria-live="polite"
      aria-labelledby="cookie-banner-title"
    >
      <div className="cookie-content">
        <div className="cookie-desktop desktop">
          <div className="cookie-desktop__copy">
            <h3 id="cookie-banner-title" className="cookie-desktop__title">
              <span>{t("cookieBanner.titleLine1")}</span>
              <span>{t("cookieBanner.titleLine2")}</span>
            </h3>
            <p className="cookie-desktop__description">
              <span>{t("cookieBanner.descriptionLine1")}</span>{" "}
              <span>{t("cookieBanner.descriptionLine2")}</span>
            </p>
          </div>
          <div className="cookie-desktop__controls">
            <div className="cookie-desktop__actions">
              <button type="button" className="cookie-desktop__action" onClick={acceptAll}>
                {t("cookieBanner.accept")}
              </button>
              <span className="cookie-desktop__rule" aria-hidden="true" />
              <button
                type="button"
                className="cookie-desktop__action"
                onClick={() => setCustomizeOpen(true)}
                aria-expanded={isCustomizeOpen}
              >
                {t("cookieBanner.customize")}
              </button>
              <span className="cookie-desktop__rule" aria-hidden="true" />
              <button type="button" className="cookie-desktop__action" onClick={rejectAll}>
                {t("cookieBanner.reject")}
              </button>
            </div>
            <Link to={policyPath} className="cookie-desktop__policy" onClick={closeBanner}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M11 16L16.65 10.35L15.225 8.925L11 13.15L8.9 11.05L7.475 12.475L11 16ZM12 22.5C9.68333 21.9167 7.77083 20.5875 6.2625 18.5125C4.75417 16.4375 4 14.1333 4 11.6V5.5L12 2.5L20 5.5V11.6C20 14.1333 19.2458 16.4375 17.7375 18.5125C16.2292 20.5875 14.3167 21.9167 12 22.5ZM12 20.4C13.7333 19.85 15.1667 18.75 16.3 17.1C17.4333 15.45 18 13.6167 18 11.6V6.875L12 4.625L6 6.875V11.6C6 13.6167 6.56667 15.45 7.7 17.1C8.83333 18.75 10.2667 19.85 12 20.4Z"
                  fill="#A5D8FF"
                />
              </svg>
              <span>{t("cookieBanner.policy")}</span>
            </Link>
          </div>
        </div>

        <div className="cookie-mobile mobile">
          <div className="cookie-mobile__copy">
            <h3 id="cookie-banner-title-mobile" className="cookie-mobile__title">
              {t("cookieBanner.mobileTitle")}
            </h3>
            <p className="cookie-mobile__description">
              {t("cookieBanner.mobileDescription")}
            </p>
          </div>
          <button type="button" className="cookie-mobile__accept" onClick={acceptAll}>
            {t("cookieBanner.accept")}
          </button>
          <Link to={policyPath} className="cookie-mobile__policy" onClick={closeBanner}>
            <span>{t("cookieBanner.policy")}</span>
            <span className="cookie-mobile__policy-line" aria-hidden="true" />
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M11 16L16.65 10.35L15.225 8.925L11 13.15L8.9 11.05L7.475 12.475L11 16ZM12 22.5C9.68333 21.9167 7.77083 20.5875 6.2625 18.5125C4.75417 16.4375 4 14.1333 4 11.6V5.5L12 2.5L20 5.5V11.6C20 14.1333 19.2458 16.4375 17.7375 18.5125C16.2292 20.5875 14.3167 21.9167 12 22.5ZM12 20.4C13.7333 19.85 15.1667 18.75 16.3 17.1C17.4333 15.45 18 13.6167 18 11.6V6.875L12 4.625L6 6.875V11.6C6 13.6167 6.56667 15.45 7.7 17.1C8.83333 18.75 10.2667 19.85 12 20.4Z"
                fill="#A5D8FF"
              />
            </svg>
          </Link>
          <div className="cookie-mobile__footer-actions">
            <button type="button" className="cookie-mobile__footer-link" onClick={rejectAll}>
              {t("cookieBanner.reject")}
            </button>
            <span className="cookie-mobile__footer-line" aria-hidden="true" />
            <button
              type="button"
              className="cookie-mobile__footer-link"
              onClick={() => setCustomizeOpen(true)}
              aria-expanded={isCustomizeOpen}
            >
              {t("cookieBanner.customize")}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );

  const modalPortal = isCustomizeOpen ? createPortal(
    (
      <div className="cookie-modal" role="dialog" aria-modal="true" aria-labelledby="cookie-modal-title">
        <div className="cookie-modal-content" ref={modalRef}>
          <div className="cookie-modal-header">
            <h3 id="cookie-modal-title">{t("cookieBanner.settingsTitle")}</h3>
            <ModalCloseButton
              className="close"
              aria-label={t("cookieBanner.close")}
              onClick={() => setCustomizeOpen(false)}
            />
          </div>
          <div className="cookie-modal-body">
            <div className="row between">
              <div className="pref-info">
                <span className="title">{t("cookieBanner.necessaryTitle")}</span>
                <span className="desc">{t("cookieBanner.necessaryDescription")}</span>
              </div>
              <label className="cookie-toggle" aria-disabled>
                <input type="checkbox" checked readOnly aria-label={t("cookieBanner.necessaryTitle")} />
                <span className="cookie-slider"></span>
              </label>
            </div>
            <div className="row between">
              <div className="pref-info">
                <span className="title">{t("cookiePolicy.cookies.functional.title")}</span>
                <span className="desc">{t("cookiePolicy.cookies.functional.description")}</span>
              </div>
              <label className="cookie-toggle">
                <input type="checkbox" checked={localPrefs.functional} onChange={() => onToggle('functional')} aria-label={t("cookiePolicy.cookies.functional.title")} />
                <span className="cookie-slider"></span>
              </label>
            </div>
            <div className="row between">
              <div className="pref-info">
                <span className="title">{t("cookiePolicy.cookies.analytics.title")}</span>
                <span className="desc">{t("cookiePolicy.cookies.analytics.description")}</span>
              </div>
              <label className="cookie-toggle">
                <input type="checkbox" checked={localPrefs.analytics} onChange={() => onToggle('analytics')} aria-label={t("cookiePolicy.cookies.analytics.title")} />
                <span className="cookie-slider"></span>
              </label>
            </div>
            <div className="row between">
              <div className="pref-info">
                <span className="title">{t("cookiePolicy.cookies.marketing.title")}</span>
                <span className="desc">{t("cookiePolicy.cookies.marketing.description")}</span>
              </div>
              <label className="cookie-toggle">
                <input type="checkbox" checked={localPrefs.marketing} onChange={() => onToggle('marketing')} aria-label={t("cookiePolicy.cookies.marketing.title")} />
                <span className="cookie-slider"></span>
              </label>
            </div>
          </div>
          <div className="cookie-modal-footer">
            <button className="btn-link" onClick={rejectAll}>{t("cookieBanner.necessaryOnly")}</button>
            <button className="btn-link" onClick={() => { savePrefs(localPrefs); setCustomizeOpen(false); }}>{t("cookieBanner.save")}</button>
          </div>
        </div>
      </div>
    ),
    document.body
  ) : null;

  return (
    <>
      {bannerPortal}
      {modalPortal}
    </>
  );
};

export default CookieBanner;
